import React from "react"
import { Link } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"
import { v4 as uuidv4 } from "uuid"
import { capitalize, cloneDeep } from "lodash"
import scrollToElement from "utilities/scrollToElement"

// components
import { Tooltip, Flex, Popover } from "antd"
import RJCommentList from "../RJCommentList/RJCommentList"
import useRegistryBadges from "../Badges/useRegistryBadges/useRegistryBadges"
import ResultsTableCreateModal from "../ResultsTableCreateModal/ResultsTableCreateModal"
import Modal from "components/Modal/Modal"

// constants
import { outcomeMeasuresDataSchema } from "../../widgets/RJOutcomeMeasures/schemas/dataSchema"
import DeleteForkSvg from "../../../assets/DeleteForkSvg"

// hooks
import useAuthoringViewStore from "../../../hooks/stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "../../../hooks/stores/useAuthoringCommentsStore"
import useAuthoringDataStore from "../../../hooks/stores/useAuthoringDataStore"
import useAuthoringVersionStore from "../../../hooks/stores/useAuthoringVersionStore"

// constants
import { SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring"
import { orphanCommentKeys } from "../../widgets/utils"

// styles
import "./LabelMenu.scss"

const LabelMenu = (props) => {
  const {
    // id,
    showMenu,
    setShowMenu,
    jpath,
    label,
    style = {},
    isForked,
    // for badges and fork button
    registryKeys,
    forkedRegistryKey,
    hideFork = false,
    hideComment = false,
    canCopyOutcomeMeasures,
    canCopyCtgOutcomeMeasures,
    canCopyAndConsolidateOMs,
    isLocked,
    jpathDotNotation,
    value,
    tooltip,
    tooltipUrl,
    tooltipUrlTitle,
    showLabel = true,
    forkedFormViewId,
    ...moreProps
  } = props

  const {
    readOnly,
    showRegistryBadges,
    formView,
    formSection,
    refreshFormData,
    setRefreshFormData,
    setShowDeleteForkModal,
    setForkPathToDelete,
    setForkRegToDelete,
    setFormStatus,
    setLoadingForm,
  } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      showRegistryBadges: state.showRegistryBadges,
      formView: state.formView,
      formSection: state.formSection,
      refreshFormData: state.refreshFormData,
      setRefreshFormData: state.setRefreshFormData,
      setFormStatus: state.setFormStatus,
      setShowDeleteForkModal: state.setShowDeleteForkModal,
      setForkPathToDelete: state.setForkPathToDelete,
      setForkRegToDelete: state.setForkRegToDelete,
      setLoadingForm: state.setLoadingForm,
    }))
  )

  const { getBadges } = useRegistryBadges()

  const { openComment, commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
      commentsMap: state.commentsMap,
    }))
  )

  const { selectedVersion } = useAuthoringVersionStore(
    useShallow((state) => ({
      selectedVersion: state.selectedVersion,
    }))
  )

  const { formData, setFormData, forkedStudyData, setForkedStudyData, overrideData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      setFormData: state.setFormData,
      forkedStudyData: state.forkedStudyData,
      setForkedStudyData: state.setForkedStudyData,
      overrideData: state.overrideData,
    }))
  )

  const [openCommentsForm, setOpenCommentsForm] = React.useState(false)
  const [openSortOmModal, setOpenSortOmModal] = React.useState(false)
  const [showCreateMeasure, setShowCreateMeasure] = React.useState(false)
  const [view, setView] = React.useState("ctg")
  const [rightPosition, setRightPosition] = React.useState(-207)
  const [commentJpathOverride, setCommentJpathOverride] = React.useState(null)

  const openCommentRef = React.useRef(null)
  const commentListRef = React.useRef()
  const labelMenuRef = React.useRef()

  const fieldComments = React.useMemo(() => {
    if (commentsMap) {
      if (commentsMap[jpath]) {
        let fieldComments = commentsMap[jpath]
        return fieldComments
      } else {
        return []
      }
    }
  }, [commentsMap])

  React.useEffect(() => {
    if (formView) {
      let view = formView.id
      if (view === "global") view = "ctg"
      else if (view === "eudra") view = "eudract"
      setView(view)
    }
  }, [formView])

  React.useEffect(() => {
    let rootContainer = document.getElementById("root-container")

    if (commentListRef?.current?.getBoundingClientRect) {
      let rootLeft = rootContainer.getBoundingClientRect().left
      let commentLeft = commentListRef.current?.getBoundingClientRect().left

      let rootRight = rootContainer.getBoundingClientRect().right
      let commentRight = commentListRef.current?.getBoundingClientRect().right

      if (rootLeft > commentLeft) {
        let leftPadding = 24
        let diff = rootLeft - commentLeft
        setRightPosition(rightPosition - diff - leftPadding)
      } else if (commentRight > rootRight) {
        let leftPadding = 24
        let diff = commentRight - rootRight
        setRightPosition(rightPosition + diff + leftPadding)
      }
    }
  }, [commentListRef.current, openComment])

  React.useEffect(() => {
    let code = openComment?.slice(0, 2)
    const targetElement = document.getElementById(openComment)

    if (openComment === jpath) {
      setOpenCommentsForm(true)
      setCommentJpathOverride(null)
    } else if (
      !targetElement &&
      (orphanCommentKeys[code] === jpath ||
        (openComment?.includes("ParticipantFlowGroup") && jpath === "root_results_participant_flow") ||
        (openComment?.includes("BaselineReportingGroup") && jpath === "root_results_baseline_characteristics") ||
        (openComment?.includes("OutcomeRptGroup") && jpath === "root_results_outcome_measures") ||
        (openComment?.includes("ReportedEvents") && jpath === "root_results_adverse_events"))
    ) {
      setOpenCommentsForm(true)
      setCommentJpathOverride(openComment)
    }
  }, [openComment, jpath])

  React.useEffect(() => {
    openCommentRef.current = openComment
  }, [openComment])

  /** functions for forked fields */
  // Field that can be forked - should show forked button.
  const fieldIsForkable = React.useMemo(() => {
    let isForkable = false
    if (
      // isLocked !== true && // TODO: uncomment after discussion
      // field has to be original field, not forked field
      !isForked &&
      // field can only be forked on a registry view (not global)
      forkedFormViewId !== "global" &&
      registryKeys?.length > 1
    ) {
      const registriesToFork = registryKeys.reduce((acc, regKey) => {
        // const pathKey = `${jpath}_${regKey}`
        if (regKey === "global") {
          return acc
        }
        acc.push(regKey)
        return acc
      }, [])
      // field has more than one registry that has not been forked
      if (registriesToFork?.length > 1) {
        isForkable = true
      }
    }
    return isForkable
  }, [forkedFormViewId, isForked])

  /**
   * Show badges (using registryKeys):
   * * When show badges toggle "off":
   * * * on global view,
   *     show fields that have been forked,
   *     either  original or forked fields.
   * * * on registry views,
   *     show forked fields only,
   *     and on the registry view where it was forked.
   * * When show badges toggle "on".
   */

  const onForkField = async () => {
    const fsd = forkedStudyData ? { ...forkedStudyData } : {}
    if (fsd?.[forkedFormViewId]) {
      fsd[forkedFormViewId] = {
        ...fsd[forkedFormViewId],
        [jpathDotNotation]: value || "",
      }
    } else {
      fsd[forkedFormViewId] = {
        [jpathDotNotation]: value || "",
      }
    }
    await setForkedStudyData(fsd)
  }

  const registryBadges = React.useMemo(() => {
    if (
      registryKeys &&
      ((forkedFormViewId === "global" && forkedRegistryKey) ||
        forkedFormViewId === forkedRegistryKey ||
        showRegistryBadges)
    ) {
      return getBadges(isForked && forkedFormViewId !== "global" ? [forkedRegistryKey] : registryKeys)
    }
    return null
  }, [formView, isForked, showRegistryBadges, forkedRegistryKey])

  // show fork button on fields that can be forked
  const forkButton = React.useMemo(() => {
    if ((showMenu || openCommentsForm) && hideFork === false && !selectedVersion && !readOnly && fieldIsForkable) {
      return (
        <Tooltip title={`Fork for ${formView.text}`}>
          <div
            style={{ marginBottom: 3 }}
            className="menu-icon fal fa-code-merge"
            onClick={onForkField}
          />
        </Tooltip>
      )
    }
    return null
  }, [showMenu, openCommentsForm, hideFork, selectedVersion, readOnly, formView, isForked, forkedRegistryKey])

  const deleteForkedButton = React.useMemo(() => {
    if (!readOnly && isForked) {
      let registryId = forkedRegistryKey?.toUpperCase()
      if (registryId === "EUDRA") registryId = "EUDRACT"
      else if (registryId === "EUPAS") registryId = "EMA RWD"

      return (
        <>
          <Tooltip title={`Remove ${registryId} forked field`}>
            <span className="delete-fork-wrapper">
              <DeleteForkSvg
                onClick={() => {
                  setShowDeleteForkModal(true)
                  setForkPathToDelete(jpathDotNotation)
                  setForkRegToDelete(forkedRegistryKey)
                }}
              />
            </span>
          </Tooltip>
        </>
      )
    }
    return null
  }, [isForked, formView, formSection, readOnly, hideFork, forkedRegistryKey])

  const commentsButton = React.useMemo(() => {
    let numOfComments = 0
    if (fieldComments)
      numOfComments = fieldComments?.filter((comment) => {
        if (comment.field_key === jpath) {
          if (comment.form_view && comment.form_view !== formView.id && formView.id !== "global") {
            return false
          }
          return true
        }
        return false
      }).length

    if (showMenu || openCommentsForm || numOfComments > 0) {
      let dataTourAttr = {}
      if (numOfComments > 0) {
        dataTourAttr["data-tour"] = "label-menu-comments-btn"
      }

      return (
        <i
          className={`menu-icon rjs-form-icon ${
            numOfComments > 0 ? "fas fa-comment-alt-dots" : "fas fa-comment-alt-medical"
          }`}
          onClick={() => {
            setOpenCommentsForm(true)
          }}
          {...dataTourAttr}
        />
      )
    }
    return null
  }, [fieldComments, showMenu, openCommentsForm, selectedVersion])

  const handleAddOutcomeMeasure = (applyAll) => {
    let tempData = { ...formData }

    let omData = tempData?.results?.outcome_measures
    let protocolOutcomeMeasures = []

    if (tempData?.protocol?.study_outcome_measures?.outcome_measures) {
      protocolOutcomeMeasures = tempData.protocol.study_outcome_measures.outcome_measures
    }

    if (!omData?.outcome_measures_table) {
      omData.outcome_measures_table = {
        ctg: {
          arms: [],
          outcome_measures: [],
        },
        eudract: {
          arms: [],
          subject_analysis_sets: [],
          outcome_measures: [],
        },
      }
    }

    if (protocolOutcomeMeasures) {
      protocolOutcomeMeasures.forEach((pom) => {
        let outcomeMeasure = {
          id: `om-${uuidv4()}`,
        }
        Object.keys(omData.outcome_measures_table).forEach((registry) => {
          let omSchema =
            outcomeMeasuresDataSchema.properties.outcome_measures_table.properties[registry].properties.outcome_measure
              .properties

          if (applyAll === true || registry === view) {
            Object.keys(omSchema).forEach((key) => {
              if (pom[key] || key === "measure_type") {
                let selectedKey = key
                let schemaKey = key

                if (key === "measure_type") selectedKey = "type"

                outcomeMeasure[schemaKey] = pom[selectedKey]
              } else {
                outcomeMeasure[key] = omSchema[key].value || ""
              }
            })

            const cloneOm = cloneDeep(outcomeMeasure)

            omData.outcome_measures_table[registry].outcome_measures = [
              ...omData.outcome_measures_table[registry].outcome_measures,
              cloneOm,
            ]
          }
        })
      })
    }
    saveAndRefreshField(tempData)
  }

  const copyCtgOutcomeMeasureToCtis = () => {
    let tempData = { ...formData }

    let protocolOutcomeMeasures = []

    if (tempData?.protocol?.study_outcome_measures?.outcome_measures) {
      protocolOutcomeMeasures = tempData.protocol.study_outcome_measures.outcome_measures
    }
    tempData.protocol.study_outcome_measures.outcome_measures_ctis = protocolOutcomeMeasures
    saveAndRefreshField(tempData)
  }

  const copyAndConsolidateCtgOutcomeMeasure = () => {
    let tempData = { ...formData }

    let protocolOutcomeMeasures = []
    const primary = []
    const secondary = []

    if (tempData?.protocol?.study_outcome_measures?.outcome_measures) {
      protocolOutcomeMeasures = tempData.protocol.study_outcome_measures.outcome_measures
      protocolOutcomeMeasures.forEach((om) => {
        if (om.type === "Primary") primary.push(om.title)
        if (om.type === "Secondary") secondary.push(om.title)
      })
    }
    let res = ""
    if (primary.length > 0) {
      res = "Primary Outcome Measures: " + primary.join(", ") + "\n"
    }
    if (secondary.length > 0) {
      res += "Secondary Outcome Measures: " + secondary.join(", ")
    }

    tempData.protocol.study_outcome_measures.eupas_outcomes = res
    saveAndRefreshField(tempData)
  }

  const copyCtgToJrctOutcomeMeasures = ({ omType = [], omFields = [] }) => {
    let tempData = { ...formData }

    let protocolOutcomeMeasures = []
    const values = []

    if (tempData?.protocol?.study_outcome_measures?.outcome_measures) {
      protocolOutcomeMeasures = tempData.protocol.study_outcome_measures.outcome_measures

      protocolOutcomeMeasures.forEach((om, index) => {
        let str = []

        omType.forEach((type) => {
          if (type === om?.type) {
            omFields.forEach((fieldKey) => {
              if (om[fieldKey]) {
                let label = capitalize(fieldKey).replaceAll("_", " ")
                str.push(`${label}: ${om[fieldKey]}`)
              }
            })
          }
        })

        if (str.length > 0) {
          str = str.join(" - ")
          let prefix = `Outcome Measure ${index + 1} - ` + str
          values.push(prefix)
        }
      })
    }

    let pathArray = jpathDotNotation.split(".")

    let obj = tempData

    pathArray.forEach((path, index) => {
      if (index !== pathArray.length - 1) {
        obj = obj[path]
      } else {
        obj[path] = values.join(", ")
      }
    })

    saveAndRefreshField(tempData)
  }

  const copyCtgInterventionsToJrctInterventions = () => {
    let tempData = { ...formData }

    let values = []

    if (tempData?.protocol?.study_arms?.interventions) {
      values = tempData.protocol.study_arms.interventions
        .filter((Intervention) => Intervention)
        .map((Intervention, index) => {
          return `Intervention ${index + 1}: Title: ${Intervention.title || "(Title)"}, Type: ${
            Intervention.type || "(Type)"
          }, Description: ${Intervention.description || "(Description)"}`
        })
    }

    let pathArray = jpathDotNotation.split(".")
    let obj = tempData

    pathArray.forEach((path, index) => {
      if (index !== pathArray.length - 1) {
        obj = obj[path]
      } else {
        obj[path] = values.join("\n\n")
      }
    })

    saveAndRefreshField(tempData)
  }

  const copyCtisInExCriteriaToJrctInEx = ({ type }) => {
    let tempData = { ...formData }

    let values = []

    if (type === "inclusion") {
      if (tempData?.protocol?.study_description?.principle_inclusion_criteria) {
        tempData.protocol.study_description.principle_inclusion_criteria.forEach((item) => {
          if (item.inclusion_criteria) {
            values.push(item.inclusion_criteria)
          }
        })
      }

      tempData.protocol.study_description.inclusion_criteria = values.join(", ")
    } else if (type === "exclusion") {
      if (tempData?.protocol?.study_description?.principle_exlusion_criteria) {
        tempData.protocol.study_description.principle_exlusion_criteria.forEach((item) => {
          if (item.exclusion_criteria) {
            values.push(item.exclusion_criteria)
          }
        })
      }

      tempData.protocol.study_description.exclusion_criteria = values.join(", ")
    }

    saveAndRefreshField(tempData)
  }

  const copyAndConsolidateGlobalOfficialTitleAndAcronym = () => {
    let tempData = { ...formData }

    let res = []
    if (overrideData?.["protocol.study_identification.official_title"]) {
      res.push(`${overrideData?.["protocol.study_identification.official_title"]}`)
    } else if (tempData?.protocol?.study_identification?.official_title) {
      res.push(`${tempData.protocol.study_identification.official_title}`)
    }

    if (tempData?.protocol?.study_identification?.acronym) {
      res.push(`(${tempData.protocol.study_identification.acronym})`)
    }

    tempData.protocol.study_identification.emarwd_official_title_and_acronym = res.join(" ")
    saveAndRefreshField(tempData)
  }

  const copyCtgProtocolSecondaryIdsToEupas = () => {
    let tempData = { ...formData }

    let res = []
    let secondaryIds = tempData?.protocol?.study_identification?.secondary_ids

    if (secondaryIds?.length > 0) {
      secondaryIds.forEach((item) => {
        let str = ""
        if (item.secondary_id_type) {
          str += `${item.secondary_id_type}: `
        }
        if (item.id) {
          str += `${item.id}`
        } else {
          str += "(ID)"
        }

        res.push(str)
      })
    }

    tempData.protocol.study_identification.other_study_ids = res.join(", ")

    saveAndRefreshField(tempData)
  }

  const saveAndRefreshField = (data) => {
    setLoadingForm(true)
    setFormData(data)
    setTimeout(() => {
      setRefreshFormData(!refreshFormData)
      setLoadingForm(false)
    }, 1000)
  }

  const onSortResultOms = async () => {
    const scrollToField = () => {
      const container = document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]
      const element = document.getElementById("root_results_outcome_measures-container")

      scrollToElement({
        scrollableElement: container,
        targetElement: element,
        offset: 100,
      })
    }

    const registries = ["ctg", "eudract"]

    const tempData = { ...formData }
    const omData = tempData?.results?.outcome_measures?.outcome_measures_table

    registries.forEach((registry) => {
      if (omData?.[registry]) {
        let oms = omData[registry]?.outcome_measures || []
        if (oms) {
          const primary = []
          const secondary = []
          const preSpecified = []
          const postHoc = []
          const other = []

          oms.forEach((om) => {
            let mt = om.measure_type
            if (mt === "Primary") {
              primary.push(om)
            } else if (mt === "Secondary") {
              secondary.push(om)
            } else if (mt === "Other pre-specified") {
              preSpecified.push(om)
            } else if (mt === "Post-hoc") {
              postHoc.push(om)
            } else {
              other.push(om)
            }
          })

          tempData.results.outcome_measures.outcome_measures_table[registry].outcome_measures = [
            ...primary,
            ...secondary,
            ...preSpecified,
            ...postHoc,
            ...other,
          ]
        }
      }
    })

    setFormData(tempData)
    setOpenSortOmModal(false)
    setFormStatus("loading")
    await new Promise((r) => setTimeout(r, 500))
    setFormStatus("ready")
    await new Promise((r) => setTimeout(r, 2000))
    scrollToField()
  }

  const renderFnBtns = () => {
    return moreProps.customFnProps.map((item, i) => {
      const { tooltip, fnKey, fnArgs } = item

      let func = () => {}

      if (fnKey === "ctg-oms") func = copyCtgToJrctOutcomeMeasures
      else if (fnKey === "jrct-interventions") func = copyCtgInterventionsToJrctInterventions
      else if (fnKey === "jrct-in-ex-criteria") func = copyCtisInExCriteriaToJrctInEx
      else if (fnKey === "copy-ctg-protocol-om-to-results") func = () => setShowCreateMeasure(true)
      else if (fnKey === "copy-and-consolidate-ctg-protocol-om-to-eupas") func = copyAndConsolidateCtgOutcomeMeasure
      else if (fnKey === "copy-ctg-protocol-om-to-ctis") func = copyCtgOutcomeMeasureToCtis
      else if (fnKey === "sort-result-oms-by-type") func = () => setOpenSortOmModal(true)
      else if (fnKey === "copy-and-consolidate-global-official-title-and-acronym")
        func = copyAndConsolidateGlobalOfficialTitleAndAcronym
      else if (fnKey === "copy-ctg-protocol-secondary-ids-to-eupas") func = copyCtgProtocolSecondaryIdsToEupas

      let iconClassName = ["menu-icon"]
      if (item.icon) iconClassName.push(item.icon)
      else iconClassName.push("fal fa-copy")
      return (
        <Tooltip
          title={tooltip}
          key={`${fnKey}-${i}`}
        >
          <i
            className={iconClassName.join(" ")}
            onClick={() => func(fnArgs)}
          ></i>
        </Tooltip>
      )
    })
  }

  let content = (
    <div style={{ maxWidth: 300, wordBreak: "break-word", whiteSpace: "pre-line" }}>
      {tooltip}
      <div className="tooltip__footer">
        <Link
          to={tooltipUrl}
          target="_blank"
          className="--link"
        >
          {tooltipUrlTitle}
        </Link>
      </div>
    </div>
  )

  let fieldLabel = `${label}`
  if (moreProps?.registries?.[formView?.id]?.title) fieldLabel = moreProps.registries[formView?.id].title

  return (
    <Flex
      className="rj-label-container"
      style={{
        paddingBottom: label ? "auto" : ".5rem",
      }}
      gap={7}
      align="center"
      wrap
      ref={labelMenuRef}
    >
      {label && showLabel && <p className="rj-label">{fieldLabel}</p>}
      {tooltip && (
        <Popover
          content={content}
          title={null}
        >
          <i className="fal fa-info-circle rjs-form-icon" />
        </Popover>
      )}
      {/* {hideComment !== true && commentsButton} */}
      {commentsButton}
      {forkButton}
      {deleteForkedButton}
      {showMenu && !readOnly && moreProps.customFnProps && renderFnBtns()}
      {registryBadges}
      {openCommentsForm && (
        <div
          ref={commentListRef}
          className="animate__animated animate__fadeIn animate__fadeInUp animate__faster rjs-label-comment-container"
          style={{ right: rightPosition }}
        >
          <RJCommentList
            jpath={commentJpathOverride ? commentJpathOverride : jpath}
            label={label}
            openForm={openCommentsForm}
            setOpenForm={setOpenCommentsForm}
            setShowMenu={setShowMenu}
          />
        </div>
      )}
      {moreProps?.customFnProps?.some((item) => item.fnKey === "copy-ctg-protocol-om-to-results") && (
        <ResultsTableCreateModal
          header="Copy Outcome Measures"
          onSubmit={handleAddOutcomeMeasure}
          showModal={showCreateMeasure}
          setShowModal={setShowCreateMeasure}
        />
      )}
      {jpath === "root_results_outcome_measures" && (
        <Modal
          title={"Sort Outcome Measures/End Point Types?"}
          message={
            <div>
              <p>
                Outcome Measures will be sorted in the following order: Primary, Secondary, Other Pre-specified,
                Post-hoc.
              </p>
              {/* <br/> */}
              <p>Note: This action cannot be undone.</p>
            </div>
          }
          width={440}
          open={openSortOmModal}
          type={"delete"}
          onCancel={() => setOpenSortOmModal(false)}
          onOk={onSortResultOms}
          okText="Sort"
        />
      )}
    </Flex>
  )
}

export default LabelMenu
